import { InboxFilterSets, getInboxItemsPerPage } from 'components/LeadInbox/helpers';
import { generatePath } from 'react-router';
import { useUrlSearchParams } from 'hooks/useUrlSearchParams';
import { WidgetType } from 'lib/enum/widget-type.enum';
import { generateUrlWithQuery } from 'lib/utils/url.utils';

export const BRANDS_AUDIENCE_SETTINGS_URL_PART = 'settings';
export const UUIDv4ValidationSyntax = '([0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12})';
export const UUID4_REGEXP = new RegExp(UUIDv4ValidationSyntax);
export const UUID4_LENGTH = 36;

export enum ParamName {
    activationToken = 'activationToken',
    brandsCampaignId = `brandsCampaignId`,
    brandsAudienceId = `brandsAudienceId`,
    businessAccountId = `businessAccountId`,
    entityId = `entityId`,
    entitySlug = 'entitySlug',
    importerConfigId = `importerConfigId`,
    jobCampaignId = `jobCampaignId`,
    jobGroupId = `jobGroupId`,
    talentId = `talentId`,
    phoneNumberId = `phoneNumberId`,
    phaseId = `phaseId`,
    phaseStrategyId = `phaseStrategyId`,
    segmentId = 'segmentId',
    userId = `userId`,
    vacancyId = `vacancyId`,
    talentpoolId = `talentpoolId`,
    talentListId = `talentListId`,
    draftBrandsCampaignId = `draftBrandsCampaignId`,
    journeyId = `journeyId`,
    widgetId = `widgetId`,
    socialCampaignId = `socialCampaignId`,
    taskadeProjectId = `taskadeProjectId`,
    agentId = `agentId`,
    versionId = `versionId`,
    connectorId = 'connectorId',
    channelId = 'channelId',
    automationId = 'automationId',
    folderId = 'folderId',
    documentId = 'documentId',
}

export enum QueryParamName {
    duplicateWidgetId = 'duplicateWidgetId',
    duplicateWhatsappTemplateId = 'duplicateWhatsappTemplateId',
}

export const Param = Object.values(ParamName).reduce<Record<ParamName, string>>((acc, key) => {
    return {
        ...acc,
        [key]: `:${ParamName[key]}`,
    };
}, {} as any);

export const UUID_V4_PARAMS = [
    ParamName.userId,
    ParamName.businessAccountId,
    ParamName.jobGroupId,
    ParamName.jobCampaignId,
    ParamName.vacancyId,
    ParamName.importerConfigId,
    ParamName.talentListId,
    ParamName.brandsAudienceId,
    ParamName.phaseStrategyId,
    ParamName.phaseId,
    ParamName.brandsCampaignId,
    ParamName.draftBrandsCampaignId,
    ParamName.talentId,
    ParamName.segmentId,
    ParamName.phoneNumberId,
    ParamName.journeyId,
    ParamName.entityId,
    ParamName.widgetId,
];

export enum RouteName {
    Home = 'Home',
    Dashboard = 'Dashboard',
    DashboardLegacy = 'DashboardLegacy',
    AiHub = 'AiHub',
    AiHubConversationalAgents = 'AiHubConversationalAgents',
    AiHubDashboard = 'AiHubDashboard',
    Analytics = 'Analytics',
    AnalyticsWebsite = 'AnalyticsWebsite',
    AnalyticsCampaign = 'AnalyticsCampaign',
    AnalyticsKpi = 'AnalyticsKpi',
    BusinessAccountGeneralSettings = 'BusinessAccountGeneralSettings',
    BusinessAccountTagsSettings = 'BusinessAccountTagsSettings',
    BusinessAccountTeamSettings = 'BusinessAccountTeamSettings',
    PartnerList = 'PartnerList',
    ConnectionList = 'ConnectionList',
    Campaigns = 'Campaigns',
    Jobs = 'Jobs',
    JobGroupList = 'JobGroupList',
    JobCampaignList = 'JobCampaignList',
    JobCampaignCreate = 'JobCampaignCreate',
    JobCampaignCreateFromVacancy = 'JobCampaignCreateFromVacancy',
    JobCampaignDuplicate = 'JobCampaignDuplicate',
    JobCampaignDetails = 'JobCampaignDetails',
    JobCampaignPerformance = 'JobCampaignPerformance',
    JobCampaignPerformanceLegacy = 'JobCampaignPerformanceLegacy',
    JobCampaignConfiguration = 'JobCampaignConfiguration',
    JobCampaignEdit = 'JobCampaignEdit',
    JobCampaignEditStep = 'JobCampaignEditStep',
    JobsCampaignManagementReport = 'JobsCampaignManagementReport',
    JobsPerformance = 'JobsPerformance',
    JobsAccountPerformance = 'JobsAccountPerformance',
    JobPerformance = 'JobPerformance',
    JobPerformanceLegacy = 'JobPerformanceLegacy',
    JobBudget = 'JobBudget',
    JobGeneralSettingsList = 'JobGeneralSettingsList',
    JobImporter = 'JobImporter',
    JobImporterVacancyList = 'JobImporterVacancyList',
    JobImporterConfigList = 'JobImporterConfigList',
    JobImporterConfigCreate = 'JobImporterConfigCreate',
    JobImporterConfigEdit = 'JobImporterConfigEdit',
    JobImporterLogList = 'JobImporterLogList',
    JobImporterSettingsList = 'JobImporterSettingsList',
    JobsSettings = 'JobsSettings',
    JobsGeneralSettings = 'JobsGeneralSettings',
    JobsTeamSettings = 'JobsTeamSettings',
    Brands = 'Brands',
    BrandsSettings = 'BrandsSettings',
    BrandsPerformance = 'BrandsPerformance',
    BrandsAccountPerformance = 'BrandsAccountPerformance',
    BrandsAudienceList = 'BrandsAudienceList',
    BrandsAudienceListNew = 'BrandsAudienceListNew',
    BrandsAudiencePerformance = 'BrandsAudiencePerformance',
    BrandsAudiencePerformanceLegacy = 'BrandsAudiencePerformanceLegacy',
    BrandsAudienceEdit = 'BrandsAudienceEdit',
    BrandsAudienceTimeline = 'BrandsAudienceTimeline',
    BrandsAudienceSettings = 'BrandsAudienceSettings',
    BrandsAudienceSettingsBudget = 'BrandsAudienceSettingsBudget',
    BrandsAudienceSettingsChannels = 'BrandsAudienceSettingsChannels',
    BrandsAudienceSettingsPhases = 'BrandsAudienceSettingsPhases',
    BirdEmailSettings = 'BirdEmailSettings',
    BirdEmailSettingsHealth = 'BirdEmailSettingsHealth',
    PhaseStrategyCreate = 'PhaseStrategyCreate',
    PhaseStrategyDuplicate = 'PhaseStrategyDuplicate',
    PhaseStrategyDetails = 'PhaseStrategyDetails',
    PhaseStrategyEdit = 'PhaseStrategyEdit',
    PhaseStrategyEditStep = 'PhaseStrategyEditStep',
    BrandsCampaignList = 'BrandsCampaignList',
    BrandsCampaignCreate = 'BrandsCampaignCreate',
    DraftBrandsCampaignSetup = 'DraftBrandsCampaignSetup',
    DraftBrandsCampaignSetupForGoogle = 'DraftBrandsCampaignSetupForGoogle',
    DraftBrandsCampaignSetupStep = 'DraftBrandsCampaignSetupStep',
    BrandsCampaignDetails = 'BrandsCampaignDetails',
    BrandsCampaignPerformance = 'BrandsCampaignPerformance',
    BrandsCampaignPerformanceLegacy = 'BrandsCampaignPerformanceLegacy',
    BrandsCampaignManagementReport = 'BrandsCampaignManagementReport',
    BrandsCampaignConfiguration = 'BrandsCampaignConfiguration',
    BrandsCampaignManagedCampaigns = 'BrandsCampaignManagedCampaigns',
    BrandsCampaignManagedCampaignsDetails = 'BrandsCampaignManagedCampaignsDetails',
    BrandsCampaignEdit = 'BrandsCampaignEdit',
    BrandsGeneralSettings = 'BrandsGeneralSettings',
    BrandsTeamSettings = 'BrandsTeamSettings',
    BrandsCampaignEditStep = 'BrandsCampaignEditStep',
    InboxGeneralSettings = 'InboxGeneralSettings',
    InboxPhoneNumbers = 'InboxPhoneNumbers',
    InboxPhoneNumberDetails = 'InboxPhoneNumberDetails',
    TalentsGeneralSettings = 'TalentsGeneralSettings',
    TalentsCustomFieldsSettings = 'TalentsCustomFieldsSettings',
    TalentsPerformance = 'TalentsPerformance',
    TalentsPerformanceNative = 'TalentsPerformanceNative',
    LeadTeamSettings = 'LeadTeamSettings',
    LeadAutomation = 'LeadAutomation',
    LeadAutomationSettings = 'LeadAutomationSettings',
    AutomationCreate = 'AutomationCreate',
    AutomationEdit = 'AutomationEdit',
    LeadJourneysSettings = 'LeadJourneysSettings',
    LeadJourneyDetail = 'LeadJourneyDetail',
    LeadJourneyCreate = 'LeadJourneyCreate',
    Leads = 'Leads',
    Talents = 'Talents',
    TalentsList = 'TalentsList',
    Talentpools = 'Talentpools',
    Segments = 'Segments',
    SegmentDetails = 'SegmentDetails',
    SegmentsCreate = 'SegmentsCreate',
    SegmentsEdit = 'SegmentsEdit',
    TalentLists = 'TalentLists',
    Talentpool = 'Talentpool',
    TalentList = 'TalentList',
    TalentpoolSettings = 'TalentpoolSettings',
    TalentListSettings = 'TalentListSettings',
    LeadListLegacy = 'LeadListLegacy',
    TalentProfile = 'LeadProfile',
    TalentsTeamSettings = 'TalentsTeamSettings',
    LeadInbox = 'LeadInbox',
    OtysCredentials = 'OtysCredentials',
    LeadInboxATSRedirect = 'LeadInboxATSRedirect',
    ContentLibrary = 'ContentLibrary',
    ContentLibraryImageCreate = 'ContentLibraryImageCreate',
    ContentLibraryHeadlineListLegacy = 'ContentLibraryHeadlineListLegacy',
    ContentLibraryHeadlineCreate = 'ContentLibraryHeadlineCreate',
    ContentLibraryHeadlineCreateNew = 'ContentLibraryHeadlineCreateNew',
    ContentLibraryMessageListLegacy = 'ContentLibraryMessageListLegacy',
    ContentLibraryMessageCreate = 'ContentLibraryMessageCreate',
    ContentLibraryMessageCreateNew = 'ContentLibraryMessageCreateNew',
    ContentLibraryVideoListLegacy = 'ContentLibraryVideoListLegacy',
    ContentLibraryVideoCreate = 'ContentLibraryVideoCreate',
    ContentLibraryMediaCreate = 'ContentLibraryMediaCreate',
    ContentLibraryMediaList = 'ContentLibraryMediaList',
    ContentLibraryMessageList = 'ContentLibraryMessageList',
    ContentLibraryHeadlineList = 'ContentLibraryHeadlineList',
    ContentLibraryWhatsappTemplatesList = 'ContentLibraryWhatsappTemplatesList',
    ContentLibraryEmailTemplatesList = 'ContentLibraryEmailTemplatesList',
    ContentLibraryWhatsappTemplateCreate = 'ContentLibraryWhatsappTemplateCreate',
    BackstageBusinessAccountList = 'BackstageBusinessAccountList',
    BackstageBusinessAccountCreate = 'BackstageBusinessAccountCreate',
    BackstageRoleList = 'BackstageRoleList',
    BackstageUserList = 'BackstageUserList',
    BackstageEmployeeList = 'BackstageEmployeeList',
    BackstageLinkedinCredentials = 'BackstageLinkedinCredentials',
    BackstageCampaignErrors = 'BackstageCampaignErrors',
    BackstageCampaigns = 'BackstageCampaigns',
    BackstageManageUpdates = 'BackstageManageUpdates',
    BackstagePartnerships = 'BackstagePartnerships',
    BackstagePartnershipsDemoEmbedded = 'BackstagePartnershipsDemoEmbedded',
    LoginFailure = 'LoginFailure',
    LoginConfirm = 'LoginConfirm',
    Activation = 'Activation',
    LinkedinRedirect = 'LinkedinRedirect',
    AuditEntry = 'AuditEntry',
    UserSettings = 'UserSettings',
    EmbeddedNoAccess = 'EmbeddedNoAccess',
    ConversionToolbox = 'ConversionToolbox',
    Widgets = 'Widgets',
    WidgetsList = 'WidgetsList',
    WidgetCreateForm = 'WidgetCreateForm',
    WidgetCreateChat = 'WidgetCreateChat',
    WidgetCreateButton = 'WidgetCreateButton',
    WidgetEditChat = 'WidgetEditChat',
    WidgetEditForm = 'WidgetEditForm',
    WidgetEditButton = 'WidgetEditButton',
    Taskade = 'Taskade',
    UnlayerEmailTemplateEditor = 'UnlayerEmailTemplateEditor',
    AiHubConversationalAgentVersions = 'AiHubConversationalAgentVersions',
    AiHubConversationalAgentVersionEdit = 'AiHubConversationalAgentVersionEdit',
    AiHubConversationalAgentVersionConfiguration = 'AiHubConversationalAgentVersionConfiguration',
    AiHubConversationalAgentVersionConfigurationEdit = 'AiHubConversationalAgentVersionConfigurationEdit',
    AiHubConversationalAgentChatSimulator = 'AiHubConversationalAgentChatSimulator',
    AiHubConversationalAgentVersionChatSimulator = 'AiHubConversationalAgentVersionChatSimulator',
    AiHubKnowledgeBase = 'AiHubKnowledgeBase',
    AiHubKnowledgeBaseFolder = 'AiHubKnowledgeBaseFolder',
    AiHubKnowledgeBaseFolderActivity = 'AiHubKnowledgeBaseFolderActivity',
    AiHubKnowledgeBaseFolderSettings = 'AiHubKnowledgeBaseFolderSettings',
    AiHubKnowledgeBaseFolderDocument = 'AiHubKnowledgeBaseFolderDocument',
    AiHubKnowledgeBaseFolderDocumentCreate = 'AiHubKnowledgeBaseFolderDocumentCreate',
    BusinessAccountProductsSettings = 'BusinessAccountProductsSettings',
    TrialAccountCreate = 'TrialAccountCreate',
}

class RouteService {
    public routePaths: Record<RouteName, string> = {
        [RouteName.Home]: '/',
        [RouteName.Dashboard]: `/b/${Param.businessAccountId}/`,
        [RouteName.DashboardLegacy]: `/b/${Param.businessAccountId}/legacy`,
        [RouteName.AiHub]: `/b/${Param.businessAccountId}/ai-hub`,
        [RouteName.Analytics]: `/b/${Param.businessAccountId}/analytics`,
        [RouteName.AnalyticsWebsite]: `/b/${Param.businessAccountId}/analytics/website`,
        [RouteName.AnalyticsCampaign]: `/b/${Param.businessAccountId}/analytics/campaign`,
        [RouteName.AnalyticsKpi]: `/b/${Param.businessAccountId}/analytics/kpi`,
        [RouteName.BusinessAccountGeneralSettings]: `/b/${Param.businessAccountId}/settings-general`, // Also hardcoded in Core / SpaUrlFactory.php
        [RouteName.BusinessAccountTagsSettings]: `/b/${Param.businessAccountId}/settings-tags`,
        [RouteName.BusinessAccountTeamSettings]: `/b/${Param.businessAccountId}/settings-team`,
        [RouteName.PartnerList]: `/b/${Param.businessAccountId}/partners`,
        [RouteName.ConnectionList]: `/b/${Param.businessAccountId}/connections`,
        [RouteName.Campaigns]: `/b/${Param.businessAccountId}/campaigns`,

        [RouteName.Jobs]: `/b/${Param.businessAccountId}/campaigns/jobs`,
        [RouteName.JobGroupList]: `/b/${Param.businessAccountId}/campaigns/jobs/group`,
        [RouteName.JobsSettings]: `/b/${Param.businessAccountId}/campaigns/jobs/settings`,
        [RouteName.JobsGeneralSettings]: `/b/${Param.businessAccountId}/campaigns/jobs/settings/general`,
        [RouteName.JobsTeamSettings]: `/b/${Param.businessAccountId}/campaigns/jobs/settings/team`,
        [RouteName.JobCampaignList]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/campaign/list`,
        [RouteName.JobCampaignCreate]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/campaign/create`,
        [RouteName.JobCampaignCreateFromVacancy]: `/b/${Param.businessAccountId}/campaigns/jobs/campaign/create/vacancy/${Param.vacancyId}`,
        [RouteName.JobCampaignDuplicate]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/campaign/create/campaign/${Param.jobCampaignId}`,
        [RouteName.JobCampaignDetails]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/campaign/${Param.jobCampaignId}/details`, // Also hardcoded in Core / SpaUrlFactory.php
        [RouteName.JobCampaignPerformance]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/campaign/${Param.jobCampaignId}/performance`,
        [RouteName.JobCampaignPerformanceLegacy]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/campaign/${Param.jobCampaignId}/performance-native`,
        [RouteName.JobCampaignConfiguration]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/campaign/${Param.jobCampaignId}/configuration`,
        [RouteName.JobCampaignEdit]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/campaign/${Param.jobCampaignId}/edit`,
        [RouteName.JobCampaignEditStep]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/campaign/${Param.jobCampaignId}/edit/:stepName`,
        [RouteName.JobsCampaignManagementReport]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/campaign/${Param.jobCampaignId}/management-report`,

        [RouteName.JobsPerformance]: `/b/${Param.businessAccountId}/campaigns/jobs/performance-native`,
        [RouteName.JobsAccountPerformance]: `/b/${Param.businessAccountId}/campaigns/jobs/performance`,
        [RouteName.JobPerformance]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/performance`,
        [RouteName.JobPerformanceLegacy]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/performance-native`,
        [RouteName.JobBudget]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/budget`, // Also hardcoded in Core / SpaUrlFactory.php
        [RouteName.JobImporter]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/importer`,
        [RouteName.JobGeneralSettingsList]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/importer/general-settings/list`,
        [RouteName.JobImporterVacancyList]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/importer/vacancies/list`,
        [RouteName.JobImporterConfigList]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/importer/importer-config/list`,
        [RouteName.JobImporterConfigCreate]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/importer/importer-config/create-new`,
        [RouteName.JobImporterConfigEdit]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/importer/importer-config/${Param.importerConfigId}/edit`,
        [RouteName.JobImporterLogList]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/importer/importer-log/list`, // Also hardcoded in Core / SpaUrlFactory.php
        [RouteName.JobImporterSettingsList]: `/b/${Param.businessAccountId}/campaigns/jobs/group/${Param.jobGroupId}/importer/importer-settings/list`,

        [RouteName.Brands]: `/b/${Param.businessAccountId}/campaigns/brands`,
        [RouteName.BrandsSettings]: `/b/${Param.businessAccountId}/campaigns/brands/settings`,
        [RouteName.BrandsPerformance]: `/b/${Param.businessAccountId}/campaigns/brands/performance-native`,
        [RouteName.BrandsAccountPerformance]: `/b/${Param.businessAccountId}/campaigns/brands/performance`,
        [RouteName.BrandsAudienceList]: `/b/${Param.businessAccountId}/campaigns/brands/audience`,
        [RouteName.BrandsAudienceListNew]: `/b/${Param.businessAccountId}/campaigns/brands/audience-new`,
        [RouteName.BrandsAudiencePerformance]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/performance`,
        [RouteName.BrandsAudiencePerformanceLegacy]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/performance-native`,
        [RouteName.BrandsAudienceEdit]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/edit`,
        [RouteName.BrandsAudienceTimeline]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/timeline`,
        [RouteName.BrandsAudienceSettings]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/${BRANDS_AUDIENCE_SETTINGS_URL_PART}`,
        [RouteName.BrandsAudienceSettingsBudget]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/budget`,
        [RouteName.BrandsAudienceSettingsChannels]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/${BRANDS_AUDIENCE_SETTINGS_URL_PART}/channels`,
        [RouteName.BrandsAudienceSettingsPhases]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/${BRANDS_AUDIENCE_SETTINGS_URL_PART}/phases`,
        [RouteName.PhaseStrategyCreate]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/phase-strategy/create`,
        [RouteName.PhaseStrategyDuplicate]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/phase-strategy/${Param.phaseStrategyId}/duplicate`,
        [RouteName.PhaseStrategyDetails]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/phase-strategy/${Param.phaseStrategyId}/details`,
        [RouteName.PhaseStrategyEdit]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/phase-strategy/${Param.phaseStrategyId}/edit`,
        [RouteName.PhaseStrategyEditStep]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/phase-strategy/${Param.phaseStrategyId}/edit/:stepName`,
        [RouteName.BrandsCampaignList]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/campaign/list`,
        [RouteName.BrandsCampaignCreate]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/campaign/create`,
        [RouteName.BrandsCampaignDetails]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/campaign/${Param.brandsCampaignId}/details`, // Also hardcoded in Core / SpaUrlFactory.php
        [RouteName.BrandsCampaignPerformance]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/campaign/${Param.brandsCampaignId}/performance`,
        [RouteName.BrandsCampaignPerformanceLegacy]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/campaign/${Param.brandsCampaignId}/performance-native`,
        [RouteName.BrandsCampaignManagementReport]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/campaign/${Param.brandsCampaignId}/management-report`,
        [RouteName.BrandsCampaignConfiguration]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/campaign/${Param.brandsCampaignId}/configuration`,
        [RouteName.BrandsCampaignManagedCampaigns]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/campaign/${Param.brandsCampaignId}/managed-social-campaign`,
        [RouteName.BrandsCampaignManagedCampaignsDetails]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/campaign/${Param.brandsCampaignId}/managed-social-campaign/${Param.socialCampaignId}`,
        [RouteName.BrandsCampaignEdit]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/campaign/${Param.brandsCampaignId}/edit`,
        [RouteName.BrandsCampaignEditStep]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/campaign/${Param.brandsCampaignId}/edit/:stepName`,
        [RouteName.BrandsGeneralSettings]: `/b/${Param.businessAccountId}/campaigns/brands/settings/general`,
        [RouteName.BrandsTeamSettings]: `/b/${Param.businessAccountId}/campaigns/brands/settings/team`,
        [RouteName.DraftBrandsCampaignSetup]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/draft/${Param.draftBrandsCampaignId}/:stepName`,
        [RouteName.DraftBrandsCampaignSetupStep]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/draft/${Param.draftBrandsCampaignId}/:stepName`,
        [RouteName.DraftBrandsCampaignSetupForGoogle]: `/b/${Param.businessAccountId}/campaigns/brands/audience/${Param.brandsAudienceId}/phase/${Param.phaseId}/draft/${Param.draftBrandsCampaignId}/for-google`,

        [RouteName.BirdEmailSettings]: `/b/${Param.businessAccountId}/bird/email/entityId/${Param.entityId}/connectorId/${Param.connectorId}/channelId/${Param.channelId}/setup`,
        [RouteName.BirdEmailSettingsHealth]: `/b/${Param.businessAccountId}/bird/email/entityId/${Param.entityId}/connectorId/${Param.connectorId}/channelId/${Param.channelId}/health`,

        [RouteName.InboxGeneralSettings]: `/b/${Param.businessAccountId}/inbox/settings-general`,
        [RouteName.InboxPhoneNumbers]: `/b/${Param.businessAccountId}/inbox/settings/phone-numbers`,
        [RouteName.InboxPhoneNumberDetails]: `/b/${Param.businessAccountId}/inbox/settings/phone-numbers/${Param.phoneNumberId}`,
        [RouteName.LeadTeamSettings]: `/b/${Param.businessAccountId}/inbox/settings/team`,
        [RouteName.LeadAutomation]: `/b/${Param.businessAccountId}/automation`,
        [RouteName.LeadAutomationSettings]: `/b/${Param.businessAccountId}/automation/automations`,
        [RouteName.AutomationCreate]: `/b/${Param.businessAccountId}/automation/automations/create`,
        [RouteName.AutomationEdit]: `/b/${Param.businessAccountId}/automation/automations/${Param.automationId}/edit`,
        [RouteName.LeadJourneysSettings]: `/b/${Param.businessAccountId}/automation/journeys`,
        [RouteName.LeadJourneyDetail]: `/b/${Param.businessAccountId}/automation/journey/detail/${Param.journeyId}`,
        [RouteName.LeadJourneyCreate]: `/b/${Param.businessAccountId}/automation/journey/create`,
        [RouteName.TalentsPerformance]: `/b/${Param.businessAccountId}/talents/performance`,
        [RouteName.TalentsPerformanceNative]: `/b/${Param.businessAccountId}/talents/performance-native`,
        [RouteName.TalentsGeneralSettings]: `/b/${Param.businessAccountId}/talents/settings-general`,
        [RouteName.TalentsTeamSettings]: `/b/${Param.businessAccountId}/talents/settings/team`,
        [RouteName.TalentsCustomFieldsSettings]: `/b/${Param.businessAccountId}/talents/settings/custom-fields`,
        [RouteName.Leads]: `/b/${Param.businessAccountId}/leads`,
        [RouteName.Talents]: `/b/${Param.businessAccountId}/talents`,
        [RouteName.TalentsList]: `/b/${Param.businessAccountId}/talents/list`,
        [RouteName.Talentpools]: `/b/${Param.businessAccountId}/talents/talentpools`,
        [RouteName.Segments]: `/b/${Param.businessAccountId}/talents/segments`,
        [RouteName.SegmentDetails]: `/b/${Param.businessAccountId}/talents/segments/${Param.segmentId}`,
        [RouteName.SegmentsCreate]: `/b/${Param.businessAccountId}/talents/segments/create`,
        [RouteName.SegmentsEdit]: `/b/${Param.businessAccountId}/talents/segments/${Param.segmentId}/edit`,
        [RouteName.TalentLists]: `/b/${Param.businessAccountId}/talents/talent-lists`,
        [RouteName.Talentpool]: `/b/${Param.businessAccountId}/talents/talentpool/${Param.talentpoolId}/list`,
        [RouteName.TalentList]: `/b/${Param.businessAccountId}/talents/talent-list/${Param.talentListId}/list`,
        [RouteName.TalentpoolSettings]: `/b/${Param.businessAccountId}/talents/talentpool/${Param.talentpoolId}/settings`,
        [RouteName.TalentListSettings]: `/b/${Param.businessAccountId}/talents/talent-list/${Param.talentListId}/settings`,
        [RouteName.LeadListLegacy]: `/b/${Param.businessAccountId}/leads/list-legacy`,
        [RouteName.TalentProfile]: `/b/${Param.businessAccountId}/talents/${Param.talentId}/profile`,
        [RouteName.LeadInbox]: `/b/${Param.businessAccountId}/inbox/:categoryName?`,
        [RouteName.OtysCredentials]: `/b/${Param.businessAccountId}/otys/credentials`,
        [RouteName.LeadInboxATSRedirect]: `/b/${Param.businessAccountId}/talents/${Param.talentId}/conversation`,
        [RouteName.ContentLibrary]: `/b/${Param.businessAccountId}/content-library`,
        [RouteName.ContentLibraryImageCreate]: `/b/${Param.businessAccountId}/content-library/add-items/media/image`,
        [RouteName.ContentLibraryHeadlineListLegacy]: `/b/${Param.businessAccountId}/content-library-legacy/headlines`,
        [RouteName.ContentLibraryHeadlineCreate]: `/b/${Param.businessAccountId}/content-library/add-items/text/headline`,
        [RouteName.ContentLibraryHeadlineCreateNew]: `/b/${Param.businessAccountId}/content-library/headlines-new/create`,
        [RouteName.ContentLibraryMessageListLegacy]: `/b/${Param.businessAccountId}/content-library-legacy/messages`,
        [RouteName.ContentLibraryMessageCreate]: `/b/${Param.businessAccountId}/content-library/add-items/text/message`,
        [RouteName.ContentLibraryMessageCreateNew]: `/b/${Param.businessAccountId}/content-library/messages-new/create`,
        [RouteName.ContentLibraryVideoListLegacy]: `/b/${Param.businessAccountId}/content-library-legacy/videos`,
        [RouteName.ContentLibraryVideoCreate]: `/b/${Param.businessAccountId}/content-library/add-items/media/video`,
        [RouteName.ContentLibraryMediaCreate]: `/b/${Param.businessAccountId}/content-library/add-items/media`,
        [RouteName.ContentLibraryMediaList]: `/b/${Param.businessAccountId}/content-library/media`,
        [RouteName.ContentLibraryMessageList]: `/b/${Param.businessAccountId}/content-library/messages`,
        [RouteName.ContentLibraryHeadlineList]: `/b/${Param.businessAccountId}/content-library/headlines`,
        [RouteName.ContentLibraryWhatsappTemplatesList]: `/b/${Param.businessAccountId}/content-library/whatsapp-templates`,
        [RouteName.ContentLibraryEmailTemplatesList]: `/b/${Param.businessAccountId}/content-library/email-templates`,
        [RouteName.ContentLibraryWhatsappTemplateCreate]: `/b/${Param.businessAccountId}/content-library/whatsapp-templates/create`,
        [RouteName.BackstageBusinessAccountList]: `/b/${Param.businessAccountId}/backstage/accounts/business-account/list`,
        [RouteName.BackstageRoleList]: `/b/${Param.businessAccountId}/backstage/accounts/role/list`,
        [RouteName.BackstageUserList]: `/b/${Param.businessAccountId}/backstage/accounts/user/list`,
        [RouteName.BackstageEmployeeList]: `/b/${Param.businessAccountId}/backstage/accounts/employee/list`,
        [RouteName.BackstageBusinessAccountCreate]: `/b/${Param.businessAccountId}/backstage/business-account/create`,
        [RouteName.BackstageLinkedinCredentials]: `/b/${Param.businessAccountId}/backstage/linkedin-credentials`,
        [RouteName.BackstageCampaigns]: `/b/${Param.businessAccountId}/backstage/campaigns`,
        [RouteName.BackstageCampaignErrors]: `/b/${Param.businessAccountId}/backstage/campaigns/campaign-errors`,
        [RouteName.BackstageManageUpdates]: `/b/${Param.businessAccountId}/backstage/campaigns/manage-updates`,
        [RouteName.BackstagePartnerships]: `/b/${Param.businessAccountId}/backstage/partnerships`,
        [RouteName.BackstagePartnershipsDemoEmbedded]: `/b/${Param.businessAccountId}/backstage/partnerships/demo-embedded`,
        [RouteName.LoginConfirm]: `/login-confirm`,
        [RouteName.LoginFailure]: `/login-failure`,
        [RouteName.Activation]: `/activation/${Param.userId}/${Param.activationToken}`, // Also hardcoded in Core / SpaUrlFactory.php
        [RouteName.LinkedinRedirect]: `/linkedin-redirect`,
        [RouteName.AuditEntry]: `/audit/${Param.entitySlug}/${Param.entityId}`,
        [RouteName.UserSettings]: `/user/${Param.userId}/settings`, // Also hardcoded in Core / SpaUrlFactory.php
        [RouteName.EmbeddedNoAccess]: `/embedded-no-access`,
        [RouteName.ConversionToolbox]: `/b/${Param.businessAccountId}/conversion-toolbox`,
        [RouteName.Widgets]: `/b/${Param.businessAccountId}/conversion-toolbox/widgets`,
        [RouteName.WidgetsList]: `/b/${Param.businessAccountId}/conversion-toolbox/widgets/list`,
        [RouteName.WidgetCreateForm]: `/b/${Param.businessAccountId}/conversion-toolbox/widgets/create-popup`,
        [RouteName.WidgetCreateChat]: `/b/${Param.businessAccountId}/conversion-toolbox/widgets/create-chat`,
        [RouteName.WidgetCreateButton]: `/b/${Param.businessAccountId}/conversion-toolbox/widgets/create-button`,
        [RouteName.WidgetEditChat]: `/b/${Param.businessAccountId}/conversion-toolbox/widgets/${Param.widgetId}/edit`,
        [RouteName.WidgetEditForm]: `/b/${Param.businessAccountId}/conversion-toolbox/widgets/${Param.widgetId}/edit-form`,
        [RouteName.WidgetEditButton]: `/b/${Param.businessAccountId}/conversion-toolbox/widgets/${Param.widgetId}/edit-button`,
        [RouteName.Taskade]: `/b/${Param.businessAccountId}/onboarding/${Param.taskadeProjectId}?`,
        [RouteName.UnlayerEmailTemplateEditor]: `/b/${Param.businessAccountId}/unlayer-email-template-editor`,
        [RouteName.AiHubConversationalAgents]: `/b/${Param.businessAccountId}/ai-hub/conversational-agents`,
        [RouteName.AiHubDashboard]: `/b/${Param.businessAccountId}/ai-hub/dashboard`,
        [RouteName.AiHubConversationalAgentVersions]: `/b/${Param.businessAccountId}/ai-hub/conversational-agents/${Param.agentId}/versions`,
        [RouteName.AiHubConversationalAgentVersionEdit]: `/b/${Param.businessAccountId}/ai-hub/conversational-agents/${Param.agentId}/versions/${Param.versionId}/edit`,
        [RouteName.AiHubConversationalAgentVersionConfiguration]: `/b/${Param.businessAccountId}/ai-hub/conversational-agents/${Param.agentId}/versions/${Param.versionId}/configuration`,
        [RouteName.AiHubConversationalAgentVersionConfigurationEdit]: `/b/${Param.businessAccountId}/ai-hub/conversational-agents/${Param.agentId}/versions/${Param.versionId}/configuration/edit`,
        [RouteName.AiHubConversationalAgentChatSimulator]: `/b/${Param.businessAccountId}/ai-hub/conversational-agents/${Param.agentId}/test`,
        [RouteName.AiHubConversationalAgentVersionChatSimulator]: `/b/${Param.businessAccountId}/ai-hub/conversational-agents/${Param.agentId}/versions/${Param.versionId}/test`,
        [RouteName.AiHubKnowledgeBase]: `/b/${Param.businessAccountId}/ai-hub/knowledge-base`,
        [RouteName.AiHubKnowledgeBaseFolder]: `/b/${Param.businessAccountId}/ai-hub/knowledge-base/${Param.folderId}`,
        [RouteName.AiHubKnowledgeBaseFolderActivity]: `/b/${Param.businessAccountId}/ai-hub/knowledge-base/${Param.folderId}/activity`,
        [RouteName.AiHubKnowledgeBaseFolderSettings]: `/b/${Param.businessAccountId}/ai-hub/knowledge-base/${Param.folderId}/settings`,
        [RouteName.AiHubKnowledgeBaseFolderDocument]: `/b/${Param.businessAccountId}/ai-hub/knowledge-base/${Param.folderId}/documents/${Param.documentId}`,
        [RouteName.AiHubKnowledgeBaseFolderDocumentCreate]: `/b/${Param.businessAccountId}/ai-hub/knowledge-base/${Param.folderId}/documents/create`,
        [RouteName.BusinessAccountProductsSettings]: `/b/${Param.businessAccountId}/settings-products`,
        [RouteName.TrialAccountCreate]: `/trial-account-create`,
    };

    public getHomePathname = (): string => this.routePaths[RouteName.Home];

    public getHomeBusinessAccountPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.Dashboard], { businessAccountId });

    public getHomeLegacyBusinessAccountPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.DashboardLegacy], { businessAccountId });

    public getAiHubPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.AiHub], { businessAccountId });

    public getAnalyticsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.Analytics], { businessAccountId });

    public getAnalyticsWebsitePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.AnalyticsWebsite], { businessAccountId });

    public getAnalyticsCampaignPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.AnalyticsCampaign], { businessAccountId });

    public getAnalyticsKpiPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.AnalyticsKpi], { businessAccountId });

    public getBusinessAccountGeneralSettingsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BusinessAccountGeneralSettings], {
            businessAccountId,
        });

    public getBusinessAccountTeamSettingsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BusinessAccountTeamSettings], {
            businessAccountId,
        });

    public getBusinessAccountTagsSettingsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BusinessAccountTagsSettings], {
            businessAccountId,
        });

    public getBusinessAccountProductsSettingsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BusinessAccountProductsSettings], {
            businessAccountId,
        });

    public getPartnersPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.PartnerList], {
            businessAccountId,
        });

    public getConnectionsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ConnectionList], {
            businessAccountId,
        });

    public getConnectionPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ConnectionList], {
            businessAccountId,
        });

    public getJobsPathname = (businessAccountId: string): string => {
        return generatePath(this.routePaths[RouteName.Jobs], { businessAccountId });
    };

    public getJobGroupListPathname = (businessAccountId: string): string => {
        return generatePath(this.routePaths[RouteName.JobGroupList], { businessAccountId });
    };

    public getJobsSettingsPathname = (businessAccountId: string): string => {
        return generatePath(this.routePaths[RouteName.JobsSettings], { businessAccountId });
    };

    public getJobsGeneralSettingsPathname = (businessAccountId: string): string => {
        return generatePath(this.routePaths[RouteName.JobsGeneralSettings], { businessAccountId });
    };

    public getJobsTeamSettingsPathname = (businessAccountId: string): string => {
        return generatePath(this.routePaths[RouteName.JobsTeamSettings], { businessAccountId });
    };

    public getJobCampaignListPathname = (businessAccountId: string, jobGroupId: string): string =>
        generatePath(this.routePaths[RouteName.JobCampaignList], {
            businessAccountId,
            jobGroupId,
        });

    public getJobCampaignCreatePathname = (businessAccountId: string, jobGroupId: string): string =>
        generatePath(this.routePaths[RouteName.JobCampaignCreate], {
            businessAccountId,
            jobGroupId,
        });

    public getJobCampaignCreateFromVacancyPathname = (businessAccountId: string, vacancyId: string): string =>
        generatePath(this.routePaths[RouteName.JobCampaignCreateFromVacancy], {
            businessAccountId,
            vacancyId,
        });

    public getJobCampaignDuplicatePathname = (
        businessAccountId: string,
        jobGroupId: string,
        jobCampaignId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.JobCampaignDuplicate], {
            businessAccountId,
            jobGroupId,
            jobCampaignId,
        });

    public getJobCampaignDetailsPathname = (
        businessAccountId: string,
        jobGroupId: string,
        jobCampaignId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.JobCampaignDetails], {
            businessAccountId,
            jobGroupId,
            jobCampaignId,
        });

    public getJobCampaignPerformancePathname = (
        businessAccountId: string,
        jobGroupId: string,
        jobCampaignId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.JobCampaignPerformance], {
            businessAccountId,
            jobGroupId,
            jobCampaignId,
        });

    public getJobCampaignConfigurationPathname = (
        businessAccountId: string,
        jobGroupId: string,
        jobCampaignId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.JobCampaignConfiguration], {
            businessAccountId,
            jobGroupId,
            jobCampaignId,
        });

    public getJobCampaignEditPathname = (
        businessAccountId: string,
        jobGroupId: string,
        jobCampaignId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.JobCampaignEdit], {
            businessAccountId,
            jobGroupId,
            jobCampaignId,
        });

    public getJobCampaignEditStepPathname = (
        businessAccountId: string,
        jobGroupId: string,
        jobCampaignId: string,
        stepName: string,
    ): string =>
        generatePath(this.routePaths[RouteName.JobCampaignEditStep], {
            businessAccountId,
            jobGroupId,
            jobCampaignId,
            stepName,
        });

    public getJobsAccountPerformancePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.JobsAccountPerformance], {
            businessAccountId,
        });

    public getJobPerformancePathname = (businessAccountId: string, jobGroupId: string): string =>
        generatePath(this.routePaths[RouteName.JobPerformance], {
            businessAccountId,
            jobGroupId,
        });

    public getJobBudgetPathname = (businessAccountId: string, jobGroupId: string): string =>
        generatePath(this.routePaths[RouteName.JobBudget], {
            businessAccountId,
            jobGroupId,
        });

    public getJobImporterPathname = (businessAccountId: string, jobGroupId: string): string =>
        generatePath(this.routePaths[RouteName.JobImporter], {
            businessAccountId,
            jobGroupId,
        });

    public getJobImporterVacancyListPathname = (businessAccountId: string, jobGroupId: string): string =>
        generatePath(this.routePaths[RouteName.JobImporterVacancyList], {
            businessAccountId,
            jobGroupId,
        });

    public getJobImporterConfigListPathname = (businessAccountId: string, jobGroupId: string): string =>
        generatePath(this.routePaths[RouteName.JobImporterConfigList], {
            businessAccountId,
            jobGroupId,
        });

    public getJobImporterConfigCreatePathname = (businessAccountId: string, jobGroupId: string): string =>
        generatePath(this.routePaths[RouteName.JobImporterConfigCreate], {
            businessAccountId,
            jobGroupId,
        });

    public getJobImporterConfigEditPathname = (
        businessAccountId: string,
        jobGroupId: string,
        importerConfigId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.JobImporterConfigEdit], {
            businessAccountId,
            jobGroupId,
            importerConfigId,
        });

    public getJobImporterLogListPathname = (businessAccountId: string, jobGroupId: string): string =>
        generatePath(this.routePaths[RouteName.JobImporterLogList], {
            businessAccountId,
            jobGroupId,
        });

    public getJobImporterSettingsListPathname = (businessAccountId: string, jobGroupId: string): string =>
        generatePath(this.routePaths[RouteName.JobImporterSettingsList], {
            businessAccountId,
            jobGroupId,
        });

    public getJobGeneralSettingsListPathname = (businessAccountId: string, jobGroupId: string): string =>
        generatePath(this.routePaths[RouteName.JobGeneralSettingsList], {
            businessAccountId,
            jobGroupId,
        });

    public getBrandsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.Brands], {
            businessAccountId,
        });

    public getBrandsSettingsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BrandsSettings], {
            businessAccountId,
        });

    public getBrandsAccountPerformancePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BrandsAccountPerformance], {
            businessAccountId,
        });

    public getBrandsAudiencePerformancePathname = (businessAccountId: string, brandsAudienceId: string): string =>
        generatePath(this.routePaths[RouteName.BrandsAudiencePerformance], {
            businessAccountId,
            brandsAudienceId,
        });

    public getBrandsAudienceListPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BrandsAudienceList], {
            businessAccountId,
        });

    public getBrandsAudienceEditPathname = (businessAccountId: string, brandsAudienceId: string): string =>
        generatePath(this.routePaths[RouteName.BrandsAudienceEdit], {
            businessAccountId,
            brandsAudienceId,
        });

    public getBrandsAudienceTimelinePathname = (businessAccountId: string, brandsAudienceId: string): string =>
        generatePath(this.routePaths[RouteName.BrandsAudienceTimeline], {
            businessAccountId,
            brandsAudienceId,
        });

    public getBrandsAudienceSettingsPathname = (businessAccountId: string, brandsAudienceId: string): string =>
        generatePath(this.routePaths[RouteName.BrandsAudienceSettings], {
            businessAccountId,
            brandsAudienceId,
        });

    public getBrandsAudienceSettingsBudgetPathname = (businessAccountId: string, brandsAudienceId: string): string =>
        generatePath(this.routePaths[RouteName.BrandsAudienceSettingsBudget], {
            businessAccountId,
            brandsAudienceId,
        });

    public getBrandsAudienceSettingsChannelsPathname = (businessAccountId: string, brandsAudienceId: string): string =>
        generatePath(this.routePaths[RouteName.BrandsAudienceSettingsChannels], {
            businessAccountId,
            brandsAudienceId,
        });

    public getBrandsAudienceSettingsPhasesPathname = (businessAccountId: string, brandsAudienceId: string): string =>
        generatePath(this.routePaths[RouteName.BrandsAudienceSettingsPhases], {
            businessAccountId,
            brandsAudienceId,
        });

    public getBrandsProductGeneralSettingsViewPathname = (businessAccountId: string): string => {
        return generatePath(this.routePaths[RouteName.BrandsGeneralSettings], { businessAccountId });
    };

    public getBrandsProductTeamSettingsViewPathname = (businessAccountId: string): string => {
        return generatePath(this.routePaths[RouteName.BrandsTeamSettings], { businessAccountId });
    };

    public getPhaseStrategyCreatePathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.PhaseStrategyCreate], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
        });

    public getPhaseStrategyDuplicatePathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        phaseStrategyId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.PhaseStrategyDuplicate], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            phaseStrategyId,
        });

    public getPhaseStrategyDetailsPathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        phaseStrategyId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.PhaseStrategyDetails], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            phaseStrategyId,
        });

    public getPhaseStrategyEditPathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        phaseStrategyId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.PhaseStrategyEdit], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            phaseStrategyId,
        });

    public getPhaseStrategyEditStepPathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        phaseStrategyId: string,
        stepName: string,
    ): string =>
        generatePath(this.routePaths[RouteName.PhaseStrategyEditStep], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            phaseStrategyId,
            stepName,
        });

    public getBrandsCampaignListPathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.BrandsCampaignList], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
        });

    public getBrandsCampaignCreatePathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.BrandsCampaignCreate], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
        });

    public getDraftBrandsCampaignSetupPathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        draftBrandsCampaignId: string,
        stepName: 'details' | 'setup' | 'creatives' = 'details',
    ): string =>
        generatePath(this.routePaths[RouteName.DraftBrandsCampaignSetup], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            draftBrandsCampaignId,
            stepName,
        });

    public getDraftBrandsCampaignSetupForGooglePathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        draftBrandsCampaignId: string,
        stepName: 'details' | 'setup' | 'creatives' = 'details',
    ): string =>
        generatePath(this.routePaths[RouteName.DraftBrandsCampaignSetupForGoogle], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            draftBrandsCampaignId,
            stepName,
        });

    public getDraftBrandsCampaignSetupStepPathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        draftBrandsCampaignId: string,
        stepName: string,
    ): string =>
        generatePath(this.routePaths[RouteName.DraftBrandsCampaignSetupStep], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            draftBrandsCampaignId,
            stepName,
        });

    public getBrandsCampaignDetailsPathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        brandsCampaignId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.BrandsCampaignDetails], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            brandsCampaignId,
        });

    public getBrandsCampaignManagementReportPathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        brandsCampaignId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.BrandsCampaignManagementReport], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            brandsCampaignId,
        });

    public getJobsCampaignManagementReportPathname = (
        businessAccountId: string,
        jobGroupId: string,
        jobCampaignId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.JobsCampaignManagementReport], {
            businessAccountId,
            jobGroupId,
            jobCampaignId,
        });

    public getBrandsCampaignPerformancePathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        brandsCampaignId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.BrandsCampaignPerformance], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            brandsCampaignId,
        });

    public getBrandsCampaignConfigurationPathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        brandsCampaignId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.BrandsCampaignConfiguration], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            brandsCampaignId,
        });

    public getBrandsCampaignManagedCampaignsPathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        brandsCampaignId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.BrandsCampaignManagedCampaigns], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            brandsCampaignId,
        });

    public getBrandsCampaignManagedCampaignDetailsPathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        brandsCampaignId: string,
        socialCampaignId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.BrandsCampaignManagedCampaignsDetails], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            brandsCampaignId,
            socialCampaignId,
        });

    public getBrandsCampaignEditPathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        brandsCampaignId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.BrandsCampaignEdit], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            brandsCampaignId,
        });

    public getBrandsCampaignEditStepPathname = (
        businessAccountId: string,
        brandsAudienceId: string,
        phaseId: string,
        brandsCampaignId: string,
        stepName: string,
    ): string =>
        generatePath(this.routePaths[RouteName.BrandsCampaignEditStep], {
            businessAccountId,
            brandsAudienceId,
            phaseId,
            brandsCampaignId,
            stepName,
        });

    public getTalentsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.TalentsList], {
            businessAccountId,
        });

    public getTalentsBasePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.Talents], {
            businessAccountId,
        });

    public getInboxGeneralSettingsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.InboxGeneralSettings], {
            businessAccountId,
        });

    public getTalentsGeneralSettingsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.TalentsGeneralSettings], {
            businessAccountId,
        });

    public getTalentsPerformancePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.TalentsPerformance], {
            businessAccountId,
        });

    public getTalentsPerformanceNativePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.TalentsPerformanceNative], {
            businessAccountId,
        });

    public getInboxPhoneNumbersPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.InboxPhoneNumbers], {
            businessAccountId,
        });

    public getInboxPhoneNumberDetailsPathname = (businessAccountId: string, phoneNumberId: string): string =>
        generatePath(this.routePaths[RouteName.InboxPhoneNumberDetails], {
            businessAccountId,
            phoneNumberId,
        });

    public getLeadTeamSettingsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.LeadTeamSettings], {
            businessAccountId,
        });

    public getTalentsTeamSettingsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.TalentsTeamSettings], {
            businessAccountId,
        });

    public getTalentsCustomFieldsSettingsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.TalentsCustomFieldsSettings], {
            businessAccountId,
        });

    public getLeadAutomationPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.LeadAutomation], {
            businessAccountId,
        });

    public getLeadAutomationSettingsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.LeadAutomationSettings], {
            businessAccountId,
        });

    public getAutomationCreatePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.AutomationCreate], {
            businessAccountId,
        });

    public getAutomationEditPathname = (businessAccountId: string, automationId: string): string =>
        generatePath(this.routePaths[RouteName.AutomationEdit], {
            businessAccountId,
            automationId,
        });

    public getLeadFlowsSettingsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.LeadJourneysSettings], {
            businessAccountId,
        });

    public getLeadJourneyDetailPathname = (businessAccountId: string, journeyId: string): string =>
        generatePath(this.routePaths[RouteName.LeadJourneyDetail], {
            businessAccountId,
            journeyId,
        });

    public getLeadJourneyCreatePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.LeadJourneyCreate], {
            businessAccountId,
        });

    public getTalentsListPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.TalentsList], {
            businessAccountId,
        });

    public getTalentpoolsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.Talentpools], {
            businessAccountId,
        });

    public getSegmentsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.Segments], {
            businessAccountId,
        });

    public getSegmentDetailsPathname = (businessAccountId: string, segmentId: string): string =>
        generatePath(this.routePaths[RouteName.SegmentDetails], {
            businessAccountId,
            segmentId,
        });

    public getSegmentCreatePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.SegmentsCreate], {
            businessAccountId,
        });

    public getSegmentEditPathname = (businessAccountId: string, segmentId: string): string =>
        generatePath(this.routePaths[RouteName.SegmentsEdit], {
            businessAccountId,
            segmentId,
        });

    public getTalentListsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.TalentLists], {
            businessAccountId,
        });

    public getTalentpoolPathname = (businessAccountId: string, talentpoolId: string): string =>
        generatePath(this.routePaths[RouteName.Talentpool], {
            businessAccountId,
            talentpoolId,
        });

    public getTalentListPathname = (businessAccountId: string, talentListId: string): string =>
        generatePath(this.routePaths[RouteName.TalentList], {
            businessAccountId,
            talentListId,
        });

    public getTalentpoolSettingsPathname = (businessAccountId: string, talentpoolId: string): string =>
        generatePath(this.routePaths[RouteName.TalentpoolSettings], {
            businessAccountId,
            talentpoolId,
        });

    public getTalentListSettingsPathname = (businessAccountId: string, talentListId: string): string =>
        generatePath(this.routePaths[RouteName.TalentListSettings], {
            businessAccountId,
            talentListId,
        });

    public getTalentProfilePathname = (businessAccountId: string, talentId: string): string =>
        generatePath(this.routePaths[RouteName.TalentProfile], {
            businessAccountId,
            talentId,
        });

    public getLeadInboxPathname = (businessAccountId: string, userId: string): string => {
        const defaultFilterSet = InboxFilterSets.primaryFilter(userId);
        const itemsPerPage = getInboxItemsPerPage();
        const filtersMap = Object.entries({ ...defaultFilterSet, itemsPerPage });
        const newHistoryLocationSearch = useUrlSearchParams.applyFiltersHelper(filtersMap, window.location.search);

        const path = generatePath(this.routePaths[RouteName.LeadInbox], {
            businessAccountId,
        });

        const pathWithParams = `${path}${newHistoryLocationSearch}`;

        return pathWithParams;
    };

    public getOtysCredentialsPathname = (businessAccountId: string, candidateOuid?: string | null): string =>
        generateUrlWithQuery(
            generatePath(this.routePaths[RouteName.OtysCredentials], {
                businessAccountId,
            }),
            {
                candidate_ouid: candidateOuid || undefined,
            },
        );

    public getTalentInboxConversationPathname = (businessAccountId: string, talentId: string): string =>
        `${generatePath(this.routePaths[RouteName.LeadInbox], {
            businessAccountId,
        })}#${talentId}`;

    public getTalentInboxAllConversationsPathname = (businessAccountId: string, talentId: string): string => {
        const allConversationsFilterSet = InboxFilterSets.allConversations;
        const itemsPerPage = getInboxItemsPerPage();
        const filtersMap = Object.entries({ ...allConversationsFilterSet, itemsPerPage });
        const searchParams = useUrlSearchParams.applyFiltersHelper(filtersMap, '');

        const path = generatePath(this.routePaths[RouteName.LeadInbox], {
            businessAccountId,
        });

        return `${path}${searchParams}#${talentId}`;
    };

    public getContentLibraryPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibrary], {
            businessAccountId,
        });

    public getCampaignsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.Campaigns], {
            businessAccountId,
        });

    public getContentLibraryImageCreatePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryImageCreate], {
            businessAccountId,
        });

    public getContentLibraryHeadlineListLegacyPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryHeadlineListLegacy], {
            businessAccountId,
        });

    public getContentLibraryHeadlineCreatePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryHeadlineCreate], {
            businessAccountId,
        });

    public getContentLibraryHeadlineCreateNewPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryHeadlineCreateNew], {
            businessAccountId,
        });

    public getContentLibraryMessageListLegacyPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryMessageListLegacy], {
            businessAccountId,
        });

    public getContentLibraryMessageCreatePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryMessageCreate], {
            businessAccountId,
        });

    public getContentLibraryMessageCreateNewPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryMessageCreateNew], {
            businessAccountId,
        });

    public getContentLibraryVideoListLegacyPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryVideoListLegacy], {
            businessAccountId,
        });

    public getContentLibraryVideoCreatePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryVideoCreate], {
            businessAccountId,
        });

    public getContentLibraryMediaCreatePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryMediaCreate], {
            businessAccountId,
        });

    public getContentLibraryMediaListPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryMediaList], {
            businessAccountId,
        });

    public getContentLibraryMessageListPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryMessageList], {
            businessAccountId,
        });

    public getContentLibraryHeadlineListPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryHeadlineList], {
            businessAccountId,
        });

    public getContentLibraryWhatsappTemplateListPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryWhatsappTemplatesList], {
            businessAccountId,
        });

    public getContentLibraryEmailTemplatesListPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ContentLibraryEmailTemplatesList], {
            businessAccountId,
        });

    public getContentLibraryWhatsappTemplateCreatePathname = (
        businessAccountId: string,
        duplicateWhatsappTemplateId?: string,
    ): string =>
        generateUrlWithQuery(
            generatePath(this.routePaths[RouteName.ContentLibraryWhatsappTemplateCreate], {
                businessAccountId,
            }),
            { [QueryParamName.duplicateWhatsappTemplateId]: duplicateWhatsappTemplateId },
        );

    public getBackstageBusinessAccountListPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BackstageBusinessAccountList], {
            businessAccountId,
        });

    public getBackstageBusinessAccountCreatePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BackstageBusinessAccountCreate], {
            businessAccountId,
        });

    public getBackstageRoleListPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BackstageRoleList], {
            businessAccountId,
        });

    public getBackstageUserListPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BackstageUserList], {
            businessAccountId,
        });

    public getBackstageEmployeesListPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BackstageEmployeeList], {
            businessAccountId,
        });

    public getBackstageLinkedinCredentialsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BackstageLinkedinCredentials], {
            businessAccountId,
        });

    public getBackstageManageUpdatesPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BackstageManageUpdates], { businessAccountId });

    public getBackstageCampaignErrorsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BackstageCampaignErrors], { businessAccountId });

    public getBackstagePartnershipsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BackstagePartnerships], { businessAccountId });

    public getBackstagePartnershipsDemoEmbeddedPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.BackstagePartnershipsDemoEmbedded], { businessAccountId });

    public getLoginConfirmPathname = (): string => this.routePaths[RouteName.LoginConfirm];

    public getActivationPathname = (userId: string, activationToken: string): string =>
        generatePath(this.routePaths[RouteName.Activation], {
            userId,
            activationToken,
        });

    public getLinkedinRedirectPathname = (): string => this.routePaths[RouteName.LinkedinRedirect];

    public getAuditEntryPathname = (entitySlug: string, entityId: string): string =>
        generatePath(this.routePaths[RouteName.AuditEntry], {
            entitySlug,
            entityId,
        });

    public getUserSettingsPathname = (userId: string): string =>
        generatePath(this.routePaths[RouteName.UserSettings], {
            userId,
        });

    public getEmbeddedNoAccessPathname = (): string => this.routePaths[RouteName.EmbeddedNoAccess];

    public getTrialAccountCreatePathname = (): string => this.routePaths[RouteName.TrialAccountCreate];

    public getConversionToolboxPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.ConversionToolbox], {
            businessAccountId,
        });

    public getWidgetsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.Widgets], {
            businessAccountId,
        });

    public getWidgetsListPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.WidgetsList], {
            businessAccountId,
        });

    public getWidgetCreatePathname = (params: {
        businessAccountId: string;
        widgetType: WidgetType;
        duplicateWidgetId?: string;
    }): string => {
        const { widgetType, businessAccountId, duplicateWidgetId } = params;
        let routePath = this.routePaths[RouteName.WidgetCreateChat];
        switch (widgetType) {
            case WidgetType.Button: {
                routePath = this.routePaths[RouteName.WidgetCreateButton];
                break;
            }
            case WidgetType.Chat: {
                routePath = this.routePaths[RouteName.WidgetCreateChat];
                break;
            }
            case WidgetType.Form: {
                routePath = this.routePaths[RouteName.WidgetCreateForm];
                break;
            }
        }
        return generateUrlWithQuery(generatePath(routePath, { businessAccountId }), {
            [QueryParamName.duplicateWidgetId]: duplicateWidgetId,
        });
    };

    public getWidgetEditPathname = (businessAccountId: string, widgetId: string, widgetType: WidgetType): string => {
        let routePath = this.routePaths[RouteName.WidgetEditChat];
        switch (widgetType) {
            case WidgetType.Chat: {
                routePath = this.routePaths[RouteName.WidgetEditChat];
                break;
            }
            case WidgetType.Form: {
                routePath = this.routePaths[RouteName.WidgetEditForm];
                break;
            }
            case WidgetType.Button: {
                routePath = this.routePaths[RouteName.WidgetEditButton];
                break;
            }
        }
        return generatePath(routePath, {
            businessAccountId,
            widgetId,
        });
    };

    public getWidgetEditChatPathname = (businessAccountId: string, widgetId: string): string =>
        generatePath(this.routePaths[RouteName.WidgetEditChat], {
            businessAccountId,
            widgetId,
        });

    public getWidgetEditFormPathname = (businessAccountId: string, widgetId: string): string =>
        generatePath(this.routePaths[RouteName.WidgetEditForm], {
            businessAccountId,
            widgetId,
        });

    public getWidgetEditButtonPathname = (businessAccountId: string, widgetId: string): string =>
        generatePath(this.routePaths[RouteName.WidgetEditButton], {
            businessAccountId,
            widgetId,
        });

    public getTaskadePathname = (businessAccountId: string, taskadeProjectId?: string): string =>
        generatePath(this.routePaths[RouteName.Taskade], {
            businessAccountId,
            taskadeProjectId,
        });

    public getUnlayerEmailTemplateEditorPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.UnlayerEmailTemplateEditor], {
            businessAccountId,
        });

    public getConversationalAgentsPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.AiHubConversationalAgents], {
            businessAccountId,
        });

    public getAiHubDashboardPathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.AiHubDashboard], {
            businessAccountId,
        });

    public getConversationalAgentVersionsPathname = (businessAccountId: string, agentId: string): string =>
        generatePath(this.routePaths[RouteName.AiHubConversationalAgentVersions], {
            businessAccountId,
            agentId,
        });

    public getConversationalAgentVersionEditPathname = (
        businessAccountId: string,
        agentId: string,
        versionId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.AiHubConversationalAgentVersionEdit], {
            businessAccountId,
            agentId,
            versionId,
        });

    public getConversationalAgentVersionConfigurationPathname = (
        businessAccountId: string,
        agentId: string,
        versionId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.AiHubConversationalAgentVersionConfiguration], {
            businessAccountId,
            agentId,
            versionId,
        });

    public getConversationalAgentVersionConfigurationEditPathname = (
        businessAccountId: string,
        agentId: string,
        versionId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.AiHubConversationalAgentVersionConfigurationEdit], {
            businessAccountId,
            agentId,
            versionId,
        });

    public getConversationalAgentChatSimulatorPathname = (businessAccountId: string, agentId: string): string =>
        generatePath(this.routePaths[RouteName.AiHubConversationalAgentChatSimulator], {
            businessAccountId,
            agentId,
        });

    public getConversationalAgentVersionChatSimulatorPathname = (
        businessAccountId: string,
        agentId: string,
        versionId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.AiHubConversationalAgentVersionChatSimulator], {
            businessAccountId,
            agentId,
            versionId,
        });

    public getAiHubKnowledgeBasePathname = (businessAccountId: string): string =>
        generatePath(this.routePaths[RouteName.AiHubKnowledgeBase], {
            businessAccountId,
        });

    public getAiHubKnowledgeBaseFolderPathname = (businessAccountId: string, folderId: string): string =>
        generatePath(this.routePaths[RouteName.AiHubKnowledgeBaseFolder], {
            businessAccountId,
            folderId,
        });

    public getAiHubKnowledgeBaseFolderActivityPathname = (businessAccountId: string, folderId: string): string =>
        generatePath(this.routePaths[RouteName.AiHubKnowledgeBaseFolderActivity], {
            businessAccountId,
            folderId,
        });

    public getAiHubKnowledgeBaseFolderSettingsPathname = (businessAccountId: string, folderId: string): string =>
        generatePath(this.routePaths[RouteName.AiHubKnowledgeBaseFolderSettings], {
            businessAccountId,
            folderId,
        });

    public getAiHubKnowledgeBaseFolderDocumentPathname = (
        businessAccountId: string,
        folderId: string,
        documentId: string,
    ): string =>
        generatePath(this.routePaths[RouteName.AiHubKnowledgeBaseFolderDocument], {
            businessAccountId,
            folderId,
            documentId,
        });

    public getAiHubKnowledgeBaseFolderDocumentCreatePathname = (businessAccountId: string, folderId: string): string =>
        generatePath(this.routePaths[RouteName.AiHubKnowledgeBaseFolderDocumentCreate], {
            businessAccountId,
            folderId,
        });

    public getBirdEmailSettingsPathname = (params: {
        businessAccountId: string;
        entityId: string;
        connectorId: string;
        channelId: string;
    }): string =>
        generatePath(this.routePaths[RouteName.BirdEmailSettings], {
            businessAccountId: params.businessAccountId,
            entityId: params.entityId,
            connectorId: params.connectorId,
            channelId: params.channelId,
        });

    public getBirdEmailSettingsHealthPathname = (params: {
        businessAccountId: string;
        entityId: string;
        connectorId: string;
        channelId: string;
    }): string =>
        generatePath(this.routePaths[RouteName.BirdEmailSettingsHealth], {
            businessAccountId: params.businessAccountId,
            entityId: params.entityId,
            connectorId: params.connectorId,
            channelId: params.channelId,
        });

    routeService: any;
}

export const routeService = new RouteService();
